<template>
  <div class="orders-bell" v-if="enabled">
    <button class="burger" type="button" @click="toggleSheet" :title="badgeTitle">
      <span v-if="pendingCount > 0" class="notification-badge bg-danger">{{ pendingCount }}</span>
      <font-awesome-icon icon="fa-solid fa-cart-shopping"/>
    </button>
    <OrdersSheet :open="sheetOpen" @close="toggleSheet" :orders="pendingOrders"
                 @accept="accept" @reject="rejectOrder" @adjust="adjust" @update-status="updateStatus"/>
  </div>
</template>

<script>
import { mapGetters, mapActions } from 'vuex';
import OrdersSheet from '@/components/orders/OrdersSheet.vue';

export default {
  name: 'OrdersBell',
  components: { OrdersSheet },
  data() {
    return {
      sheetOpen: false,
      lastCount: 0,
      bellOsc: null,
    };
  },
  computed: {
    ...mapGetters('orders', ['getPendingOrders', 'getPendingCount', 'isOnlineStoreRegistered']),
    pendingOrders() { return this.getPendingOrders; },
    pendingCount() { return this.getPendingCount; },
    enabled() {
      return this.isOnlineStoreRegistered;
    },
    badgeTitle() {
      if (this.pendingCount === 0) return 'No online orders';
      return `${this.pendingCount} pending online order${this.pendingCount>1?'s':''}`;
    }
  },
  watch: {
    pendingCount(newVal, oldVal) {
      if (newVal > oldVal) {
        this.playBell();
      }
    }
  },
  async mounted() {
    try {
      // Check if store is registered before starting polling
      const isRegistered = await this.$store.dispatch('orders/checkRegistration');
      if (isRegistered) {
        this.$store.dispatch('orders/startPolling');
      }
    } catch (e) {
      console.warn('[OrdersBell] Failed to start polling', e);
    }
  },
  methods: {
    ...mapActions('orders', {
      acceptOnlineOrderAction: 'acceptOnlineOrder',
      acceptOrderAction: 'acceptOrder',
      rejectOrderAction: 'rejectOrder',
      adjustItemsAction: 'adjustItems',
      updateOrderStatusAction: 'updateOrderStatus'
    }),
    toggleSheet() { this.sheetOpen = !this.sheetOpen; },
    accept(payload) {
      if (payload?.orderId && payload?.saleNumber) {
        return this.acceptOrderAction({
          orderId: payload.orderId,
          saleNumber: payload.saleNumber,
          reason: payload.reason,
        });
      }
      return this.acceptOnlineOrderAction(payload);
    },
    rejectOrder({ orderId, reason }) { this.rejectOrderAction({ orderId, reason }); },
    adjust({ orderId, payload }) { this.adjustItemsAction({ orderId, payload }); },
    updateStatus({ orderId, status, notes }) { this.updateOrderStatusAction({ orderId, status, notes }); },
    playBell() {
      try {
        const ctx = new (window.AudioContext || window.webkitAudioContext)();
        const o = ctx.createOscillator();
        const g = ctx.createGain();
        o.type = 'triangle';
        o.frequency.value = 880; // A5
        o.connect(g);
        g.connect(ctx.destination);
        g.gain.setValueAtTime(0.0001, ctx.currentTime);
        g.gain.exponentialRampToValueAtTime(0.2, ctx.currentTime + 0.01);
        g.gain.exponentialRampToValueAtTime(0.0001, ctx.currentTime + 0.4);
        o.start();
        o.stop(ctx.currentTime + 0.45);
      } catch (e) {
        // silent if audio context blocked
      }
    }
  }
}
</script>

<style scoped>
.burger {
  width: 40px;
  height: 40px;
  background: transparent;
  color: var(--nav-icon-color);
  font-size: 30px;
}
.notification-badge {
  position: fixed;
  margin-right: 5px;
  color: white;
  border-radius: 5px;
  padding: 1px 6px 1px 6px;
  font-size: 12px;
  opacity: 1;
  transform: scale(1);
  animation: notification-pulse 1.5s infinite;
}
@keyframes notification-pulse {
  0% { transform: scale(1); box-shadow: 0 0 0 0 rgba(51,156,255,0.8);} 
  50% { transform: scale(1.2); box-shadow: 0 0 0 10px rgba(51,156,255,0);} 
  100% { transform: scale(1); box-shadow: 0 0 0 0 rgba(51,156,255,0);} 
}
</style>

